import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from './ThemeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { syncFromUser, clearUser } = useTheme();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth header to requests
  api.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      syncFromUser(response.data, (t) => api.patch('/auth/me/theme', { theme: t }).catch(() => {}));
      
      // Fetch merchant profile if user is a merchant
      if (response.data.role === 'merchant') {
        try {
          const merchantResponse = await api.get('/merchants/me');
          setMerchant(merchantResponse.data);
        } catch (err) {
          // Merchant profile might not exist yet
          setMerchant(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Login with role validation - if allowedRoles is provided, will throw error if role doesn't match
  const login = async (email, password, allowedRoles = null) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    // Check role restrictions BEFORE persisting auth state
    if (allowedRoles && !allowedRoles.includes(userData.role)) {
      // Don't persist auth state - throw error for caller to handle
      throw {
        isRoleError: true,
        userRole: userData.role,
        response: { data: { detail: `Access denied. Your role (${userData.role}) is not allowed on this login portal.` } }
      };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    const authed = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${access_token}` } });
    syncFromUser(userData, (t) => authed.patch('/auth/me/theme', { theme: t }).catch(() => {}));
    
    // Fetch merchant profile if user is a merchant
    if (userData.role === 'merchant') {
      try {
        const merchantResponse = await api.get('/merchants/me', {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setMerchant(merchantResponse.data);
      } catch (err) {
        setMerchant(null);
      }
    }
    
    return userData;
  };

  const register = async (name, email, password, role = 'merchant') => {
    const response = await api.post('/auth/register', { name, email, password, role });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    const authed = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${access_token}` } });
    syncFromUser(userData, (t) => authed.patch('/auth/me/theme', { theme: t }).catch(() => {}));
    
    return userData;
  };

  const logout = () => {
    clearUser();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMerchant(null);
  };

  const refreshMerchant = async () => {
    if (user?.role === 'merchant' && token) {
      try {
        const response = await api.get('/merchants/me');
        setMerchant(response.data);
        return response.data;
      } catch (err) {
        setMerchant(null);
        return null;
      }
    }
    return null;
  };

  const isAdmin = () => {
    return ['super_admin', 'risk_manager', 'finance_manager'].includes(user?.role);
  };

  const isReseller = () => user?.role === 'reseller';

  const value = {
    user,
    token,
    merchant,
    loading,
    login,
    register,
    logout,
    refreshMerchant,
    isAdmin,
    isReseller,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
